import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toPng } from "html-to-image";
import {
  FaArrowLeft,
  FaChalkboard,
  FaPrint,
  FaDownload,
  FaSpinner,
  FaCheck,
  FaFileAlt,
  FaTv,
} from "react-icons/fa";
import { HouseContext } from "../../../../context/HouseContext";
import { BounceLoader } from "react-spinners";
import CartelCard from "../../../../components/CartelPropiedad/CartelCard";
import CartelCardTV from "../../../../components/CartelPropiedad/CartelCardTV";
import { formatCharacteristic, isAdminCharacteristic } from "../../../../utils/characteristicLabel";

const slugify = (str) =>
  String(str || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// El cartel se renderiza siempre a tamaño real (ver CartelCard/CartelCardTV)
// para que la impresión y la imagen descargada salgan nítidas; acá solo lo
// achicamos visualmente con un transform para que entre en pantalla. La
// escala se calcula según el ancho disponible para que se vea lo más grande
// posible.
const FORMATS = {
  paper: { widthPx: 794, cssWidth: "210mm", cssHeight: "297mm", label: "Papel (A4)" },
  tv: { widthPx: 1920, cssWidth: "1920px", cssHeight: "1080px", label: "Pantalla (TV)" },
};
const MAX_PREVIEW_SCALE = 1;

function PropertyCartel() {
  const { id } = useParams();
  const { houses } = useContext(HouseContext);
  const house = useMemo(() => houses.find((h) => h.id === id), [houses, id]);

  const [selectedImage, setSelectedImage] = useState("");
  const [checkedKeys, setCheckedKeys] = useState(() => new Set());
  const [generating, setGenerating] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [format, setFormat] = useState("paper");
  // Arranca chico a propósito: en pantallas angostas (celular) es mejor un
  // primer parpadeo chico que uno grande que desborde la pantalla mientras
  // el ResizeObserver mide el ancho real disponible.
  const [previewScale, setPreviewScale] = useState(0.3);
  const [form, setForm] = useState(null);
  // La propiedad tarda en cargar (muestra un loader), así que el contenedor
  // de la vista previa no existe todavía en el primer render. Un ref normal
  // no "avisa" cuando el nodo aparece más tarde; con un callback ref sí.
  const [previewContainerEl, setPreviewContainerEl] = useState(null);
  const previewContainerRef = useCallback((node) => setPreviewContainerEl(node), []);
  const cardRef = useRef(null);

  // Todo lo que se ve en el cartel se puede editar acá (sin tocar la
  // propiedad real): arranca precargado con los datos de la propiedad.
  useEffect(() => {
    if (!house) return;
    setForm({
      name: house.name || "",
      address: house.address || "",
      country: house.country || "",
      type: house.type || "",
      state: house.state || "",
      currency: house.currency || "$",
      price: house.price || "",
      hidePrice: true,
    });
  }, [house]);

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  useEffect(() => {
    if (!previewContainerEl) return;
    const updateScale = () => {
      const availableWidth = previewContainerEl.clientWidth - 32;
      if (!availableWidth) return;
      setPreviewScale(Math.min(availableWidth / FORMATS[format].widthPx, MAX_PREVIEW_SCALE));
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(previewContainerEl);
    return () => observer.disconnect();
  }, [previewContainerEl, format]);

  // "Estado" y "Contrato" son datos internos (no para un cartel de
  // vidriera), por eso no se ofrecen para mostrar.
  const characteristicsEntries = useMemo(
    () =>
      Object.entries(house?.characteristics || {}).filter(
        ([key, value]) => value && !isAdminCharacteristic(key)
      ),
    [house]
  );

  useEffect(() => {
    if (!house) return;
    setSelectedImage(house.images?.[0] || "");
  }, [house]);

  useEffect(() => {
    setCheckedKeys(new Set(characteristicsEntries.map(([key]) => key)));
  }, [characteristicsEntries]);

  if (!house || !form) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <BounceLoader color="#187795" size={50} />
          <p className="text-gray-500 dark:text-gray-400">Cargando propiedad...</p>
        </div>
      </div>
    );
  }

  const cartelProperty = {
    ...house,
    name: form.name,
    address: form.address,
    country: form.country,
    type: form.type,
    state: form.state,
    currency: form.hidePrice ? "" : form.currency,
    price: form.hidePrice ? "" : form.price,
  };

  const toggleKey = (key) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedCharacteristics = characteristicsEntries
    .filter(([key]) => checkedKeys.has(key))
    .map(([key, value]) => ({ key, value }));

  const qrUrl = `${window.location.origin}/propiedad/${house.id}`;
  const fileName = `Cartel-${format === "tv" ? "TV-" : ""}${slugify(form.name)}.png`;

  const esperarImagenes = async (node) => {
    const imgs = Array.from(node.querySelectorAll("img"));
    await Promise.all(
      imgs.map(async (img) => {
        try {
          if (!img.complete || img.naturalWidth === 0) {
            await new Promise((res) => {
              img.onload = res;
              img.onerror = res;
            });
          }
          if (img.decode) await img.decode();
        } catch {
          /* si una imagen falla, seguimos igual */
        }
      })
    );
  };

  const descargar = async () => {
    const node = cardRef.current;
    if (!node) return;
    setGenerating(true);
    setFeedback(null);
    try {
      await esperarImagenes(node);
      const dataUrl = await toPng(node, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setFeedback({ type: "success", msg: "Imagen descargada" });
    } catch (e) {
      console.error("Error generando el cartel:", e);
      setFeedback({ type: "error", msg: "No se pudo generar la imagen" });
    } finally {
      setGenerating(false);
    }
  };

  const imprimir = () => window.print();

  return (
    <>
    <div className="px-4 md:px-6 py-4 print:hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/dashboard/propiedades"
            className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
            title="Volver a Propiedades"
          >
            <FaArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 flex-shrink-0">
            <FaChalkboard className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Cartel para imprimir</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 capitalize truncate">{house.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={descargar}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg disabled:opacity-50"
          >
            {generating ? <FaSpinner className="w-3.5 h-3.5 animate-spin" /> : <FaDownload className="w-3.5 h-3.5" />}
            Descargar imagen
          </button>
          {format === "paper" && (
            <button
              onClick={imprimir}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary hover:bg-secondary text-white rounded-lg"
            >
              <FaPrint className="w-3.5 h-3.5" />
              Imprimir
            </button>
          )}
        </div>
      </div>

      {feedback && (
        <div
          className={`mb-4 rounded-lg px-3 py-2 text-xs ${
            feedback.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {feedback.msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-5 items-start">
        {/* Controles */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 space-y-6 lg:sticky lg:top-4">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Formato</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormat("paper")}
                className={`inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  format === "paper"
                    ? "bg-primary text-white border-primary"
                    : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                }`}
              >
                <FaFileAlt className="w-3.5 h-3.5" />
                Papel (A4)
              </button>
              <button
                type="button"
                onClick={() => setFormat("tv")}
                className={`inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  format === "tv"
                    ? "bg-primary text-white border-primary"
                    : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                }`}
              >
                <FaTv className="w-3.5 h-3.5" />
                Pantalla (TV)
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Información a mostrar
            </p>
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Tipo</label>
                  <input
                    type="text"
                    value={form.type}
                    onChange={setField("type")}
                    className="w-full text-sm px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Estado</label>
                  <input
                    type="text"
                    value={form.state}
                    onChange={setField("state")}
                    className="w-full text-sm px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-gray-400 mb-1">Título</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={setField("name")}
                  className="w-full text-sm px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Dirección</label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={setField("address")}
                    className="w-full text-sm px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Localidad</label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={setField("country")}
                    className="w-full text-sm px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 pt-1">
                <input
                  type="checkbox"
                  checked={form.hidePrice}
                  onChange={(e) => setForm((prev) => ({ ...prev, hidePrice: e.target.checked }))}
                  className="accent-primary"
                />
                Ocultar precio (no se muestra en el cartel)
              </label>
              {!form.hidePrice && (
                <div className="grid grid-cols-[auto_1fr] gap-2">
                  <select
                    value={form.currency}
                    onChange={setField("currency")}
                    className="text-sm px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="$">$</option>
                    <option value="USD">USD</option>
                  </select>
                  <input
                    type="number"
                    value={form.price}
                    onChange={setField("price")}
                    placeholder="Precio"
                    className="w-full text-sm px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Foto de portada
            </p>
            {house.images?.length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {house.images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedImage(img)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === img
                        ? "border-primary"
                        : "border-transparent hover:border-gray-300"
                    }`}
                  >
                    <img src={img} alt={`Foto ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" />
                    {selectedImage === img && (
                      <span className="absolute top-1 right-1 bg-primary text-white rounded-full p-0.5">
                        <FaCheck className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Esta propiedad no tiene fotos cargadas</p>
            )}
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Características a mostrar
            </p>
            {characteristicsEntries.length > 0 ? (
              <div className="space-y-2">
                {characteristicsEntries.map(([key, value]) => (
                  <label
                    key={key}
                    className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checkedKeys.has(key)}
                      onChange={() => toggleKey(key)}
                      className="accent-primary"
                    />
                    <span className="truncate">{formatCharacteristic(key, value)}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Sin características cargadas</p>
            )}
          </div>

          <p className="text-xs text-gray-400 dark:text-gray-500">
            El código QR lleva directo a la publicación de esta propiedad en el sitio.
          </p>
        </div>

        {/* Vista previa */}
        <div ref={previewContainerRef} className="flex justify-center min-w-0 w-full overflow-hidden">
          <div
            style={{
              width: `calc(${FORMATS[format].cssWidth} * ${previewScale})`,
              height: `calc(${FORMATS[format].cssHeight} * ${previewScale})`,
              maxWidth: "100%",
            }}
            className="overflow-hidden shadow-lg border border-gray-200"
          >
            <div style={{ transform: `scale(${previewScale})`, transformOrigin: "top left" }}>
              {format === "tv" ? (
                <CartelCardTV
                  ref={cardRef}
                  property={cartelProperty}
                  image={selectedImage}
                  selectedCharacteristics={selectedCharacteristics}
                  qrUrl={qrUrl}
                />
              ) : (
                <CartelCard
                  ref={cardRef}
                  property={cartelProperty}
                  image={selectedImage}
                  selectedCharacteristics={selectedCharacteristics}
                  qrUrl={qrUrl}
                />
              )}
            </div>
          </div>
        </div>
      </div>

    </div>

    {/* Nodo oculto a tamaño real: lo que se imprime (ver @media print en index.css).
        Fuera del contenedor print:hidden de arriba, si no también se ocultaría.
        Solo aplica al formato papel: la pantalla de TV no se imprime. */}
    {format === "paper" && (
      <div className="hidden print:block" aria-hidden="true">
        <CartelCard
          id="cartel-print-area"
          property={cartelProperty}
          image={selectedImage}
          selectedCharacteristics={selectedCharacteristics}
          qrUrl={qrUrl}
        />
      </div>
    )}
    </>
  );
}

export default PropertyCartel;
